// public/js/features/files/uploader.js
// The universal uploader (docs/asset-files.md §9): ONE modal that accepts files
// from all four paths field staff actually use — clipboard paste (screenshots),
// drag & drop, the phone camera, and the classic file picker — then shows a
// queue where each file gets a label, a document type, its own progress bar and
// its own retry. Used by asset detail (files + photos), the files explorer, the
// assets-list row menu and the map asset card, so it must stay surface-agnostic:
// everything it needs comes in through openUploader().
//
//   openUploader({ assetUUID, kind: 'file'|'photo', docTypes, onDone })

import { escapeHtml } from '../../utils/escape.js';
import { UPLOADER_CSS } from './uploader.css.js';
import {
  uploadFile, validateFile, resolveContentType, stripExtension,
  ALLOWED_TYPES, MAX_FILE_BYTES,
} from './api.js';

let stylesInjected = false;
function injectStyles() {
  if (stylesInjected) return;
  stylesInjected = true;
  const style = document.createElement('style');
  style.id = 'sbu-uploader-styles';
  style.textContent = UPLOADER_CSS;
  document.head.appendChild(style);
}

const IMAGE_TYPES = ALLOWED_TYPES.filter(t => t.startsWith('image/'));

function typeIcon(contentType) {
  if (contentType.startsWith('image/')) return 'fa-file-image';
  if (contentType === 'application/pdf') return 'fa-file-pdf';
  if (/word|msword/.test(contentType)) return 'fa-file-word';
  if (/sheet|excel/.test(contentType)) return 'fa-file-excel';
  if (/presentation|powerpoint/.test(contentType)) return 'fa-file-powerpoint';
  if (contentType === 'text/csv') return 'fa-file-csv';
  if (contentType.startsWith('text/') || contentType === 'application/json') return 'fa-file-lines';
  return 'fa-file';
}

function shortType(contentType) {
  const map = {
    'application/pdf': 'PDF', 'image/jpeg': 'JPEG', 'image/png': 'PNG', 'image/webp': 'WEBP',
    'image/gif': 'GIF', 'image/heic': 'HEIC', 'image/heif': 'HEIF',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PPTX',
    'application/msword': 'DOC', 'application/vnd.ms-excel': 'XLS',
    'text/plain': 'TXT', 'text/csv': 'CSV', 'application/json': 'JSON',
  };
  return map[contentType] || (contentType.split('/')[1] || 'FILE').toUpperCase().slice(0, 6);
}

function formatBytes(n) {
  if (!Number.isFinite(n)) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0, v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
}

// Clipboard images all arrive as "image.png" — give them a name a human can
// find again in the file list.
function nameClipboardFile(file) {
  const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
  const ext = (file.type.split('/')[1] || 'png').replace('jpeg', 'jpg');
  try {
    return new File([file], `screenshot-${stamp}.${ext}`, { type: file.type });
  } catch {
    return file; // very old browsers: keep the original name rather than fail
  }
}

export function openUploader({ assetUUID, kind = 'file', docTypes = [], onDone } = {}) {
  injectStyles();

  const isPhoto = kind === 'photo';
  const types = isPhoto ? IMAGE_TYPES : ALLOWED_TYPES;
  const usableDocTypes = isPhoto ? [] : (docTypes || []).filter(t => t && t.enabled !== false);
  const defaultDocType = usableDocTypes[0]?.docTypeId || 'other';
  const touch = (navigator.maxTouchPoints || 0) > 0;

  const items = [];
  let busy = false;
  let finished = false;

  const id = `sbu-${Math.random().toString(36).slice(2)}`;
  document.body.insertAdjacentHTML('beforeend', `
    <div class="modal fade" id="${id}" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="fa-solid ${isPhoto ? 'fa-camera' : 'fa-cloud-arrow-up'} me-2 text-primary"></i>${isPhoto ? 'Add photos' : 'Upload files'}
            </h5>
            <button type="button" class="btn-close sbu-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div class="sbu-drop" tabindex="0" role="button"
                 aria-label="Drop files here, paste from the clipboard, or click to choose files">
              <div class="sbu-drop-icon mb-1"><i class="fa-solid fa-cloud-arrow-up"></i></div>
              <div class="fw-semibold">Drop ${isPhoto ? 'photos' : 'files'} here or click to choose</div>
              <div class="sbu-drop-hint mt-1">
                You can also paste from the clipboard (Ctrl+V / Cmd+V) — screenshots included.
              </div>
            </div>
            <div class="d-flex flex-wrap gap-2 mt-2">
              <button type="button" class="btn btn-sm btn-outline-primary sbu-pick">
                <i class="fa-solid fa-folder-open me-1"></i>Choose ${isPhoto ? 'photos' : 'files'}
              </button>
              ${touch ? `
              <button type="button" class="btn btn-sm btn-outline-primary sbu-camera">
                <i class="fa-solid fa-camera me-1"></i>Take a photo
              </button>` : ''}
              <span class="ms-auto sbu-drop-hint align-self-center">
                Max ${MAX_FILE_BYTES / 1048576} MB per file
              </span>
            </div>
            <input type="file" class="d-none sbu-input" multiple accept="${types.join(',')}">
            <input type="file" class="d-none sbu-camera-input" accept="image/*" capture="environment">
            <div class="sbu-queue mt-3"></div>
          </div>
          <div class="modal-footer">
            <span class="me-auto small text-muted sbu-summary"></span>
            <button type="button" class="btn btn-secondary sbu-cancel" data-bs-dismiss="modal">Cancel</button>
            <button type="button" class="btn btn-primary sbu-upload" disabled>
              <i class="fa-solid fa-cloud-arrow-up me-1"></i>Upload
            </button>
          </div>
        </div>
      </div>
    </div>`);

  const modalEl = document.getElementById(id);
  const modal = new bootstrap.Modal(modalEl, { backdrop: 'static' });
  const q = (sel) => modalEl.querySelector(sel);
  const dropZone = q('.sbu-drop');
  const queueEl = q('.sbu-queue');
  const fileInput = q('.sbu-input');
  const cameraInput = q('.sbu-camera-input');
  const uploadBtn = q('.sbu-upload');

  // ── queue ──────────────────────────────────────────────────────────────────

  function addFiles(fileList, { fromClipboard = false } = {}) {
    let added = 0;
    for (let file of Array.from(fileList || [])) {
      if (fromClipboard) file = nameClipboardFile(file);
      const dupe = items.some(i => i.file.name === file.name && i.file.size === file.size
        && i.file.lastModified === file.lastModified && i.status !== 'done');
      if (dupe) continue;
      const contentType = resolveContentType(file);
      const check = validateFile(file, contentType);
      items.push({
        id: `f${Math.random().toString(36).slice(2)}`,
        file, contentType,
        label: stripExtension(file.name),
        docTypeId: defaultDocType,
        status: check.ok ? 'ready' : 'invalid',
        error: check.ok ? null : check.error,
        progress: 0,
        previewUrl: contentType.startsWith('image/') ? URL.createObjectURL(file) : null,
        record: null,
      });
      added++;
    }
    if (added) renderQueue();
  }

  function rowHtml(item) {
    const docOptions = usableDocTypes.map(t =>
      `<option value="${escapeHtml(t.docTypeId)}"${t.docTypeId === item.docTypeId ? ' selected' : ''}>${escapeHtml(t.label || t.docTypeId)}</option>`).join('');
    return `
      <div class="sbu-row" data-id="${item.id}">
        <div class="sbu-thumb">
          ${item.previewUrl
    ? `<img src="${escapeHtml(item.previewUrl)}" alt="">`
    : `<i class="fa-solid ${typeIcon(item.contentType)}"></i>`}
        </div>
        <div class="sbu-main">
          <div class="sbu-name" title="${escapeHtml(item.file.name)}">${escapeHtml(item.file.name)}</div>
          <div class="sbu-meta">${shortType(item.contentType)} &middot; ${formatBytes(item.file.size)}</div>
          <div class="sbu-error mt-1 d-none"></div>
          ${item.status === 'invalid' ? '' : `
          <div class="sbu-fields">
            <input type="text" class="form-control sbu-label-input" placeholder="Label" aria-label="Label">
            ${usableDocTypes.length ? `<select class="form-select sbu-doctype" aria-label="Document type">${docOptions}</select>` : ''}
          </div>`}
          <div class="progress d-none" role="progressbar" aria-label="Upload progress">
            <div class="progress-bar" style="width:0%"></div>
          </div>
        </div>
        <div class="sbu-actions">
          <i class="fa-solid fa-circle-check text-success sbu-ok d-none mt-1"></i>
          <button type="button" class="btn btn-sm btn-outline-secondary sbu-retry d-none" title="Retry">
            <i class="fa-solid fa-rotate-right"></i>
          </button>
          <button type="button" class="btn btn-sm btn-outline-secondary sbu-remove" title="Remove">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
      </div>`;
  }

  function renderQueue() {
    if (!items.length) {
      queueEl.innerHTML = '';
    } else {
      queueEl.innerHTML = items.map(rowHtml).join('');
      items.forEach(bindRow);
    }
    items.forEach(paintRow);
    updateFooter();
  }

  const rowEl = (item) => queueEl.querySelector(`[data-id="${item.id}"]`);

  function bindRow(item) {
    const row = rowEl(item);
    if (!row) return;
    // Values are assigned, never interpolated, so odd file names cannot break out.
    const labelInput = row.querySelector('.sbu-label-input');
    if (labelInput) {
      labelInput.value = item.label;
      labelInput.addEventListener('input', () => { item.label = labelInput.value; });
    }
    const docSelect = row.querySelector('.sbu-doctype');
    if (docSelect) {
      docSelect.value = item.docTypeId;
      docSelect.addEventListener('change', () => { item.docTypeId = docSelect.value; });
    }
    // A preview that cannot be decoded (HEIC on most desktops) falls back to the
    // type icon instead of showing a broken image.
    row.querySelector('img')?.addEventListener('error', () => {
      const holder = row.querySelector('.sbu-thumb');
      if (holder) holder.innerHTML = `<i class="fa-solid ${typeIcon(item.contentType)}"></i>`;
    });
    row.querySelector('.sbu-remove').addEventListener('click', () => removeItem(item));
    row.querySelector('.sbu-retry').addEventListener('click', () => retryItem(item));
  }

  function paintRow(item) {
    const row = rowEl(item);
    if (!row) return;
    row.classList.toggle('sbu-invalid', item.status === 'invalid');
    row.classList.toggle('sbu-failed', item.status === 'failed');
    row.classList.toggle('sbu-done', item.status === 'done');

    const err = row.querySelector('.sbu-error');
    err.textContent = item.error || '';
    err.classList.toggle('d-none', !item.error);

    const progress = row.querySelector('.progress');
    progress.classList.toggle('d-none', item.status !== 'uploading');
    paintProgress(item);

    row.querySelectorAll('.sbu-label-input, .sbu-doctype')
      .forEach(el => { el.disabled = item.status === 'uploading' || item.status === 'done'; });
    row.querySelector('.sbu-ok').classList.toggle('d-none', item.status !== 'done');
    row.querySelector('.sbu-retry').classList.toggle('d-none', item.status !== 'failed' || busy);
    row.querySelector('.sbu-remove').classList.toggle('d-none', item.status === 'uploading' || item.status === 'done');
  }

  function paintProgress(item) {
    const bar = rowEl(item)?.querySelector('.progress-bar');
    if (bar) bar.style.width = `${Math.round((item.progress || 0) * 100)}%`;
  }

  function removeItem(item) {
    const i = items.indexOf(item);
    if (i < 0) return;
    if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
    items.splice(i, 1);
    renderQueue();
  }

  function pendingItems() {
    return items.filter(i => i.status === 'ready' || i.status === 'failed');
  }

  function updateFooter() {
    const pending = pendingItems().length;
    const done = items.filter(i => i.status === 'done').length;
    const invalid = items.filter(i => i.status === 'invalid').length;
    uploadBtn.disabled = busy || pending === 0;
    uploadBtn.innerHTML = busy
      ? '<span class="spinner-border spinner-border-sm me-2"></span>Uploading…'
      : `<i class="fa-solid fa-cloud-arrow-up me-1"></i>Upload${pending ? ` ${pending}` : ''}`;
    const parts = [];
    if (done) parts.push(`${done} uploaded`);
    if (invalid) parts.push(`${invalid} rejected`);
    q('.sbu-summary').textContent = parts.join(' · ');
    q('.sbu-close').disabled = busy;
    q('.sbu-cancel').disabled = busy;
  }

  // ── upload ─────────────────────────────────────────────────────────────────

  async function uploadOne(item) {
    item.status = 'uploading';
    item.error = null;
    item.progress = 0;
    paintRow(item);
    try {
      item.record = await uploadFile(item.file, {
        assetUUID, kind,
        label: item.label,
        docTypeId: item.docTypeId,
        contentType: item.contentType,
        onProgress: (p) => { item.progress = p; paintProgress(item); },
      });
      item.status = 'done';
      item.progress = 1;
    } catch (e) {
      item.status = 'failed';
      item.error = e?.message || 'Upload failed';
    }
    paintRow(item);
    updateFooter();
  }

  // Sequential on purpose: one honest progress bar at a time, and a slow phone
  // link is not asked to carry five parallel uploads.
  async function startUpload() {
    if (busy) return;
    const queue = pendingItems();
    if (!queue.length) return;
    busy = true;
    updateFooter();
    for (const item of queue) await uploadOne(item);
    busy = false;
    items.forEach(paintRow);
    updateFooter();

    const ok = items.filter(i => i.status === 'done').length;
    const failed = items.filter(i => i.status === 'failed').length;
    if (failed) {
      window.showToast?.(`${ok} uploaded, ${failed} failed — use retry on the failed rows.`, 'warning');
      return; // keep the modal (and the queue) open
    }
    window.showToast?.(`${ok} ${isPhoto ? (ok === 1 ? 'photo' : 'photos') : (ok === 1 ? 'file' : 'files')} uploaded`, 'success');
    modal.hide();
  }

  async function retryItem(item) {
    if (busy) return;
    busy = true;
    updateFooter();
    await uploadOne(item);
    busy = false;
    items.forEach(paintRow);
    updateFooter();
  }

  // ── input paths ────────────────────────────────────────────────────────────

  dropZone.addEventListener('click', () => fileInput.click());
  // Programmatic focus does not always trigger :focus-visible, so mark the armed
  // state explicitly — the user must see where a paste will land.
  dropZone.addEventListener('focus', () => dropZone.classList.add('sbu-focus'));
  dropZone.addEventListener('blur', () => dropZone.classList.remove('sbu-focus'));
  dropZone.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInput.click(); }
  });
  q('.sbu-pick').addEventListener('click', () => fileInput.click());
  q('.sbu-camera')?.addEventListener('click', () => cameraInput.click());

  [fileInput, cameraInput].forEach(input => {
    input.addEventListener('change', () => {
      addFiles(input.files);
      input.value = ''; // so picking the same file twice fires again
    });
  });

  ['dragenter', 'dragover'].forEach(ev => dropZone.addEventListener(ev, (e) => {
    e.preventDefault(); e.stopPropagation();
    dropZone.classList.add('sbu-over');
  }));
  ['dragleave', 'dragend'].forEach(ev => dropZone.addEventListener(ev, () => dropZone.classList.remove('sbu-over')));
  dropZone.addEventListener('drop', (e) => {
    e.preventDefault(); e.stopPropagation();
    dropZone.classList.remove('sbu-over');
    addFiles(e.dataTransfer?.files, {});
  });
  // Dropping anywhere but the zone must not make the browser navigate away.
  ['dragover', 'drop'].forEach(ev => modalEl.addEventListener(ev, (e) => e.preventDefault()));

  modalEl.addEventListener('paste', (e) => {
    const files = Array.from(e.clipboardData?.files || []);
    if (!files.length) return;
    e.preventDefault();
    addFiles(files, { fromClipboard: true });
  });

  uploadBtn.addEventListener('click', startUpload);

  // ── lifecycle ──────────────────────────────────────────────────────────────

  // Focus the drop zone so Ctrl+V lands in the modal without an extra click.
  modalEl.addEventListener('shown.bs.modal', () => dropZone.focus());

  modalEl.addEventListener('hidden.bs.modal', () => {
    items.forEach(i => { if (i.previewUrl) URL.revokeObjectURL(i.previewUrl); });
    const records = items.filter(i => i.record).map(i => i.record);
    modalEl.remove();
    if (!finished) {
      finished = true;
      if (records.length) onDone?.(records);
    }
  }, { once: true });

  updateFooter();
  modal.show();

  return { modal, addFiles };
}
